// Image / file uploader for proposal blocks. Uses /api/proposals/upload which
// stores in Emergent Object Storage and returns a public URL.

import { useRef, useState } from "react";
import { Upload, X, Loader2, ImageIcon, FolderOpen } from "lucide-react";
import api, { API_BASE, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { AssetPickerModal } from "./AssetLibrary";

export default function ProposalImageUpload({ value, onChange, kind = "image", disabled, testId, height = 220 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (kind === "image" && !file.type.startsWith("image/")) {
      toast.error("Please pick an image file"); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/proposals/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // API_BASE ends in /api — the URL returned is already /api/public/files/{id}
      const absolute = `${window.location.origin}${data.url}`;
      onChange({ url: absolute, filename: data.filename, size: data.size, content_type: data.content_type });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (disabled) return;
    upload(e.dataTransfer.files?.[0]);
  };

  const clear = () => onChange({ url: "" });

  if (value?.url) {
    return (
      <div className="relative" data-testid={testId}>
        {kind === "image" ? (
          <img src={value.url} alt="" style={{ width: "100%", maxHeight: height * 2, objectFit: "cover", borderRadius: 8 }} />
        ) : (
          <a
            href={value.url} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 p-3"
            style={{ border: "1px solid var(--pg-border)", borderRadius: 8, fontSize: 13, color: "var(--pg-accent)" }}
          >
            {value.filename || "Attachment"}
          </a>
        )}
        {!disabled && (
          <button
            onClick={clear}
            className="absolute top-2 right-2 pg-btn pg-btn-ghost"
            style={{ padding: 4, background: "rgba(15,23,42,0.65)", color: "#fff" }}
            data-testid={`${testId}-clear`}
          >
            <X size={13} />
          </button>
        )}
      </div>
    );
  }

  return (
    <>
    <label
      onDragOver={(e) => { if (disabled) return; e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      htmlFor={testId}
      style={{
        display: "block", padding: "22px 20px",
        border: `2px dashed ${dragOver ? "var(--pg-accent)" : "var(--pg-border)"}`,
        borderRadius: 10, textAlign: "center",
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        background: dragOver ? "rgba(91,79,232,0.04)" : "transparent",
        transition: "border-color 140ms, background 140ms",
        minHeight: height,
      }}
      data-testid={testId}
    >
      {uploading ? (
        <>
          <Loader2 size={22} className="animate-spin" style={{ color: "var(--pg-accent)", margin: "10px auto 8px" }} />
          <div style={{ fontSize: 13 }}>Uploading…</div>
        </>
      ) : (
        <>
          {kind === "image" ? <ImageIcon size={22} style={{ color: "var(--pg-text-mute)", margin: "0 auto 10px" }} />
                            : <Upload size={22} style={{ color: "var(--pg-text-mute)", margin: "0 auto 10px" }} />}
          <div style={{ fontSize: 13, fontWeight: 500 }}>
            {kind === "image" ? "Drop an image here, or click to browse" : "Drop a file here, or click to browse"}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--pg-text-mute)" }}>
            Up to 10 MB · PNG, JPG, WEBP, SVG{kind === "file" ? ", PDF" : ""}
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPickerOpen(true); }}
              className="pg-btn pg-btn-ghost"
              style={{ marginTop: 12, fontSize: 12 }}
              data-testid={`${testId}-pick-library`}
            >
              <FolderOpen size={12} /> Choose from library
            </button>
          )}
        </>
      )}
      <input
        id={testId}
        ref={inputRef}
        type="file"
        accept={kind === "image" ? "image/*" : "image/*,application/pdf"}
        disabled={disabled}
        style={{ display: "none" }}
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </label>
    <AssetPickerModal
      open={pickerOpen}
      onClose={() => setPickerOpen(false)}
      accept={kind === "image" ? "image" : "any"}
      onPick={(a) => onChange({
        url: `${window.location.origin}${a.url}`,
        filename: a.original_filename,
        size: a.size,
        content_type: a.content_type,
      })}
      title={kind === "image" ? "Pick an image" : "Pick a file"}
    />
    </>
  );
}
